import { setUser, setUserClaims } from '../analytics'
import { listAssociatedFirstPartyAccounts } from '../dataAccess/accounts'
import { getAccountStatus } from '../dataAccess/accountStatuses'
import { listReadableAssetsForAccount } from '../dataAccess/assets'
import {
  listReceivedClientInvitationsForAccount,
  listSentClientInvitationsForAccount
} from '../dataAccess/clientInvitations'
import {
  listDashboardDocuments,
  listDashboardDocumentsForAccountAdmin,
  listDashboardDocumentsForUser,
  listVaultDocumentsForAccount
} from '../dataAccess/documents'
import {
  getGlobalFeatureToggleSet,
  getUserFeatureToggleSet
} from '../dataAccess/featureToggles'
import {
  listActivePrices,
  listAvailableSubscriptionPlans
} from '../dataAccess/products'
import { listReadableSharesForAccount } from '../dataAccess/shares'
import { getCompanyForAccount } from '../dataAccess/companies'
import {
  listActiveTemplateCategoriesForAccountType,
  listActiveTemplatesForAccount,
  listActiveTemplatesForCategories,
  listTemplatesForAccount
} from '../dataAccess/templates'
import { auth, firebase, fsdb } from '../firebase'
import {
  addBindingUnsubscribe,
  firesBind,
  removeAllBindings
} from './firebaseBinders'

const actions = {
  checkAuth({ dispatch, commit, state }) {
    if (state.userBinding) {
      return state.userBinding
    }

    commit(
      'set_userBinding',
      new Promise((resolve, reject) => {
        try {
          auth.onAuthStateChanged((user) => {
            setUser(user)
            commit('set_user', user || {})
            commit('set_claimsBinding', null)
            resolve(true)
            dispatch('get_claims')
          })
        } catch (e) {
          reject(e)
        }
      })
    )

    return state.userBinding
  },

  async reloadCurrentUser({ commit }) {
    await auth.currentUser.reload()
    commit('set_user', auth.currentUser)
  },

  get_claims({ dispatch, commit, state }) {
    if (state.claimsBinding) {
      return state.claimsBinding
    }

    commit(
      'set_claimsBinding',
      new Promise(async (resolve, reject) => {
        await dispatch('checkAuth')
        if (!state.user.uid) {
          commit('set_claims', {})
          resolve(true)
          return
        }
        const userMetadataRef = fsdb.collection('metadata').doc(state.user.uid)
        addBindingUnsubscribe(
          userMetadataRef.onSnapshot(async (snapshot) => {
            const user = firebase.auth().currentUser
            if (state.claimsBinding) {
              // only refresh on subsequent snapshots
              await user.getIdToken(true)
            }
            const { claims } = await user.getIdTokenResult()
            commit('set_claims', claims)
            setUserClaims(claims)
            resolve(true)
          })
        )
      })
    )

    return state.claimsBinding
  },

  async LOGOUT({ commit }) {
    removeAllBindings()
    await commit('resetState')
    auth.signOut()
  },

  async get_appUser(context) {
    const queryRefFunc = (context) =>
      fsdb.collection('users').doc(context.state.user.uid)

    return firesBind(
      'appUser',
      queryRefFunc,
      ['checkAuth', 'get_claims'],
      this._vm,
      context
    )
  },

  async get_accountStatus(context) {
    return firesBind(
      'accountStatus',
      ({ getters: { currentAccountId } }) => getAccountStatus(currentAccountId),
      ['get_claims'],
      this._vm,
      context
    )
  },

  async get_account(context) {
    const queryRefFunc = (context) =>
      fsdb
        .collection('accounts')
        .doc(context.state.selectedAccountId || context.state.claims.accountId)
    // also set the selectedAccountId
    const onExistsExtra = (context) => {
      context.commit(
        'set_selectedAccountId',
        context.state.selectedAccountId || context.state.claims.accountId
      )
    }
    // if there's no account but user is an individual, create mock account record
    const onNotExistsExtra = (context) => {
      if (context.state.claims.accountType === 'individual') {
        context.commit('set_account', {
          id: context.state.claims.accountId,
          shortName: context.state.appUser.accountShortName,
          type: context.state.claims.accountType,
          active: context.state.appUser.active
        })
      }
    }

    return firesBind(
      'account',
      queryRefFunc,
      ['get_appUser', 'get_claims'],
      this._vm,
      context,
      onExistsExtra,
      onNotExistsExtra
    )
  },

  async get_accountUsers(context) {
    const queryRefFunc = (context) =>
      fsdb
        .collection('users')
        .where(
          'accountId',
          '==',
          context.state.selectedAccountId || context.state.account.id
        )

    return firesBind(
      'accountUsers',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },

  async get_clientUsers(context) {
    const queryRefFunc = (context) =>
      fsdb
        .collection('users')
        .where('accountType', '==', 'individual')
        .where(
          'links',
          'array-contains',
          context.state.selectedAccountId || context.state.account.id
        )

    return firesBind(
      'clientUsers',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },
  async get_userTemplateCategories(context) {
    const queryRefFunc = ({
      state: {
        account: { type: accountTypeId }
      }
    }) => listActiveTemplateCategoriesForAccountType(accountTypeId)

    return firesBind(
      'userTemplateCategories',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },

  async get_availableSubscriptionPlans(context) {
    const queryRefFunc = ({
      state: {
        account: { type: accountTypeId }
      }
    }) => listAvailableSubscriptionPlans(accountTypeId)

    return firesBind(
      'availableSubscriptionPlans',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },

  async get_activePrices(context) {
    return firesBind('activePrices', listActivePrices, [], this._vm, context)
  },

  async get_accountTemplates(context) {
    const queryRefFunc = ({ state: { account } }) =>
      listTemplatesForAccount(account)

    return firesBind(
      'accountTemplates',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },

  async get_activeAccountTemplates(context) {
    const queryRefFunc = ({ state: { account } }) =>
      listActiveTemplatesForAccount(account)

    return firesBind(
      'activeAccountTemplates',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },

  async get_userTemplates(context) {
    const queryRefFunc = ({ state: { userTemplateCategories } }) =>
      listActiveTemplatesForCategories(userTemplateCategories)

    return firesBind(
      'userTemplates',
      queryRefFunc,
      ['get_userTemplateCategories'],
      this._vm,
      context
    )
  },

  async get_pendingClientUsers({ dispatch, commit, state }) {
    if (state.pendingClientUsersBinding) {
      return state.pendingClientUsersBinding
    }

    commit(
      'set_pendingClientUsersBinding',
      new Promise(async (resolve, reject) => {
        await dispatch('get_account')

        if (state.claims.isAdmin) {
          // if admin, just query directly
          try {
            const associationsRef = fsdb
              .collection('users')
              .where('accountType', '==', 'individual')
              .where(
                'pendingLinks',
                'array-contains',
                state.selectedAccountId || state.account.id
              )

            addBindingUnsubscribe(
              associationsRef.onSnapshot(
                (snapshot) => {
                  var docs = []
                  snapshot.forEach((doc) => {
                    docs.push(
                      Object.assign({ id: doc.id, '.key': doc.id }, doc.data())
                    )
                  })

                  commit('set_pendingClientUsers', docs)
                  resolve(true)
                },
                (e) => {
                  this._vm.$Sentry.captureException(e)
                  reject(e)
                }
              )
            )
          } catch (e) {
            this._vm.$Sentry.captureException(e)
            reject(e)
          }
        } else {
          // otherwise, use the limited data endpoint
          try {
            const response = await this._vm.$fireGet(
              'users/pendingClients',
              firebase.auth()
            )

            commit(
              'set_pendingClientUsers',
              response.data.map((user) => ({ ...user, '.key': user.id }))
            )
            resolve(true)
          } catch (e) {
            this._vm.$Sentry.captureException(e)
            reject(e)
          }
        }
      })
    )

    return state.pendingClientUsersBinding
  },

  async get_inactiveClientUsers({ dispatch, commit, state }) {
    if (state.inactiveClientUsersBinding) {
      return state.inactiveClientUsersBinding
    }

    commit(
      'set_inactiveClientUsersBinding',
      new Promise(async (resolve, reject) => {
        await dispatch('get_account')

        if (state.claims.isAdmin) {
          // if admin, just query directly
          try {
            const associationsRef = fsdb
              .collection('users')
              .where('accountType', '==', 'individual')
              .where(
                'inactiveLinks',
                'array-contains',
                state.selectedAccountId || state.account.id
              )

            addBindingUnsubscribe(
              associationsRef.onSnapshot(
                (snapshot) => {
                  var docs = []
                  snapshot.forEach((doc) => {
                    docs.push(
                      Object.assign({ id: doc.id, '.key': doc.id }, doc.data())
                    )
                  })

                  commit('set_inactiveClientUsers', docs)
                  resolve(true)
                },
                (e) => {
                  this._vm.$Sentry.captureException(e)
                  reject(e)
                }
              )
            )
          } catch (e) {
            this._vm.$Sentry.captureException(e)
            reject(e)
          }
        } else {
          // otherwise, use the limited data endpoint
          try {
            const response = await this._vm.$fireGet(
              'users/inactiveClients',
              firebase.auth()
            )

            commit(
              'set_inactiveClientUsers',
              response.data.map((user) => ({ ...user, '.key': user.id }))
            )
            resolve(true)
          } catch (e) {
            this._vm.$Sentry.captureException(e)
            reject(e)
          }
        }
      })
    )

    return state.inactiveClientUsersBinding
  },

  async get_knownUsers({ dispatch, commit, state }, { refresh, include } = {}) {
    // if there's something additional to be included in the response on top of the normal, make this a refresh
    /* an include would be something like templateExpertLawyers/{templateId} - it's special because there's no
       direct connection between a user and a template expert, so we have to request it be added.
       TODO BETA add docs > templates > get experts to /knownUsers - instead of knownUserIncludes, make a normal
       refresh of knownUsers grab all templates and filter to docsICanRead.templateId <> templates.expertlawyers
    */
    if (include && !(include in state.knownUsersIncludes)) {
      refresh = true
      commit('set_knownUsersIncludes', include)
    }
    await dispatch('get_appUser')
    // if there's been a refresh requested, and the user isn't an admin, clear the binding
    if (refresh && !state.claims.isAdmin) {
      commit('set_knownUsersBinding', null)
    }
    // if there's already a binding, return it
    if (state.knownUsersBinding) {
      return state.knownUsersBinding
    }
    commit(
      'set_knownUsersBinding',
      new Promise(async (resolve, reject) => {
        try {
          let usersRef
          if (state.claims.isAdmin) {
            // if the user is an admin, just give them the full users list.
            usersRef = fsdb.collection('users')
          } else {
            usersRef = fsdb.collection('knownUsers').doc(state.user.uid)
            if (refresh) {
              await this._vm.$firePost('users/knownUsers', firebase.auth(), {
                includes: Object.keys(state.knownUsersIncludes)
              })
            }
          }
          addBindingUnsubscribe(
            usersRef.onSnapshot(
              (snapshot) => {
                if (!snapshot.empty) {
                  var knownUsers
                  if (snapshot.docs) {
                    knownUsers = snapshot.docs.reduce(
                      (acc, item) => ({
                        ...acc,
                        [item.id]: {
                          id: item.id,
                          ...item.data()
                        }
                      }),
                      {}
                    )
                  } else {
                    knownUsers = {}
                    if (snapshot.data()) {
                      // TODO BETA create add key as id prop method
                      const dataWithIds = Object.entries(
                        snapshot.data()
                      ).reduce(
                        (acc, [key, item]) => ({
                          ...acc,
                          [key]: {
                            id: key,
                            ...item
                          }
                        }),
                        {}
                      )
                      knownUsers = dataWithIds
                    }
                  }
                  commit('set_knownUsers', knownUsers)
                } else {
                  commit('set_knownUsers', {})
                }
                resolve(true)
              },
              (e) => {
                this._vm.$Sentry.captureException(e)
                reject(e)
              }
            )
          )
        } catch (e) {
          this._vm.$Sentry.captureException(e)
          reject(e)
        }
      })
    )
    return state.knownUsersBinding
  },

  async get_documents(context) {
    await context.dispatch('get_account')
    let queryRefFunc = ({
      getters: { isAdmin, isAccountAdmin },
      state: {
        user,
        claims: { accountId }
      }
    }) => {
      if (isAdmin) {
        return listDashboardDocuments()
      }
      if (isAccountAdmin) {
        return listDashboardDocumentsForAccountAdmin(accountId, user)
      }
      return listDashboardDocumentsForUser(user)
    }

    return firesBind(
      'documents',
      queryRefFunc,
      ['get_appUser'],
      this._vm,
      context
    )
  },

  async get_vaultDocuments(context) {
    await context.dispatch('get_account')
    let queryRefFunc = ({
      state: {
        claims: { accountId }
      }
    }) => listVaultDocumentsForAccount(accountId)

    return firesBind(
      'vaultDocuments',
      queryRefFunc,
      ['get_appUser'],
      this._vm,
      context
    )
  },

  async get_associatedFirstParties(context) {
    return firesBind(
      'associatedFirstParties',
      ({ getters: { currentAccountId } }) =>
        listAssociatedFirstPartyAccounts(currentAccountId),
      ['get_account'],
      this._vm,
      context
    )
  },
  async get_sentClientInvitations(context) {
    return firesBind(
      'sentClientInvitations',
      ({ getters: { currentAccountId } }) =>
        listSentClientInvitationsForAccount(currentAccountId),
      ['get_account'],
      this._vm,
      context
    )
  },
  async get_receivedClientInvitations(context) {
    return firesBind(
      'receivedClientInvitations',
      ({ getters: { currentAccountId } }) =>
        listReceivedClientInvitationsForAccount(currentAccountId),
      ['get_account'],
      this._vm,
      context
    )
  },
  async get_counterpartyUsers(context) {
    const queryRefFunc = (context) =>
      fsdb
        .collection('users')
        .where(
          'links',
          'array-contains',
          context.state.selectedAccountId || context.state.account.id
        )

    return firesBind(
      'counterpartyUsers',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },
  async get_signatories(context, account) {
    return this.requestData({
      name: 'signatories',
      context,
      makeRequest: () => this.$api.getAccountSignatories(account)
    })
  },

  async get_shares(context) {
    let queryRefFunc = ({
      state: {
        claims: { accountId }
      }
    }) => listReadableSharesForAccount(accountId)

    return firesBind('shares', queryRefFunc, ['get_account'], this._vm, context)
  },
  async get_company(context) {
    let queryRefFunc = ({
      state: {
        claims: { accountId }
      }
    }) => getCompanyForAccount(accountId)

    return firesBind(
      'company',
      queryRefFunc,
      ['get_account'],
      this._vm,
      context
    )
  },

  async get_assets(context) {
    let queryRefFunc = ({
      state: {
        claims: { accountId }
      }
    }) => listReadableAssetsForAccount(accountId)
    return firesBind('assets', queryRefFunc, ['get_appUser'], this._vm, context)
  },
  async get_featureToggles({ dispatch }) {
    return Promise.all([
      dispatch('get_globalFeatureToggleSet'),
      dispatch('get_userFeatureToggleSet')
    ])
  },
  async get_userFeatureToggleSet(context) {
    let queryRefFunc = ({ state: { appUser } }) => getUserFeatureToggleSet(appUser.id)
    return firesBind(
      'userFeatureToggleSet',
      queryRefFunc,
      ['get_appUser'],
      this._vm,
      context
    )
  },
  async get_globalFeatureToggleSet(context) {
    return firesBind(
      'globalFeatureToggleSet',
      getGlobalFeatureToggleSet,
      [],
      this._vm,
      context
    )
  }
}

export default actions
